/**
 * Validators that could be used in any application
 */
import { FactoryProvider, ValueProvider } from '@angular/core';
import { Entity } from 'breeze-client';
import { UnitOfWorkBase } from '../services';
import { ValidationContextBase } from './validation-context';
import { VALIDATOR_FACTORY, parseOptionalChaining } from './validation-metadata';
import { SyncValidator } from './validator-base';

export class RequiredValidator extends SyncValidator {
  constructor(group: string, validatorContext?: Partial<ValidationContextBase>) {
    super('required', group, (value, context) => value != null && (typeof value !== 'string' || value !== ''), {
      ...validatorContext,
      messageTemplate: (validatorContext && validatorContext.messageTemplate) || '%displayName% is required',
    });
  }

  static create(group: string, context?: Partial<ValidationContextBase>): RequiredValidator {
    return new RequiredValidator(group, context);
  }
}

export class MinLengthValidator extends SyncValidator {
  private static messageTemplate = '%displayName% must be a string with at least %minLength% characters';

  constructor(group: string, validatorContext?: Partial<ValidationContextBase>) {
    super(
      'minLength',
      group,
      (value, context) => {
        if (value == null) {
          return true;
        }
        return typeof value === 'string' && value.length >= context.minLength;
      },
      {
        ...validatorContext,
        messageTemplate: (validatorContext && validatorContext.messageTemplate) || MinLengthValidator.messageTemplate,
      }
    );
  }

  static create(group: string, context?: Partial<ValidationContextBase>): MinLengthValidator {
    return new MinLengthValidator(group, context);
  }
}

export class MaxLengthValidator extends SyncValidator {
  private static messageTemplate = '%displayName% must be a string with at most %maxLength% characters';

  constructor(group: string, validatorContext?: Partial<ValidationContextBase>) {
    super(
      'maxLength',
      group,
      (value, context) => {
        if (value == null) {
          return true;
        }
        return typeof value === 'string' && value.length <= context.maxLength;
      },
      {
        ...validatorContext,
        messageTemplate: (validatorContext && validatorContext.messageTemplate) || MaxLengthValidator.messageTemplate,
      }
    );
  }

  static create(group: string, context?: Partial<ValidationContextBase>): MaxLengthValidator {
    return new MaxLengthValidator(group, context);
  }
}

export class RegExpValidator extends SyncValidator {
  private static messageTemplate = "The %displayName% '%value%' does not match '%expression%'";

  constructor(name: string, group: string, validatorContext?: Partial<ValidationContextBase>) {
    super(
      name,
      group,
      (value, context) => {
        if (value == null) {
          return true;
        }
        const expression = context.expression as RegExp;
        return typeof value === 'string' && expression.test(value);
      },
      {
        ...validatorContext,
        messageTemplate: (validatorContext && validatorContext.messageTemplate) || RegExpValidator.messageTemplate,
      }
    );
  }

  static create(name: string, group: string, context?: Partial<ValidationContextBase>): RegExpValidator {
    return new RegExpValidator(name, group, context);
  }
}

export class RangeValidator extends SyncValidator {
  private static messageTemplate = '%displayName% must be between the values of %minValue% and %maxValue%';

  constructor(group: string, validatorContext?: Partial<ValidationContextBase>) {
    super(
      'range',
      group,
      (value, context) => {
        if (value == null) {
          return true;
        }
        return value >= context.minValue && value <= context.maxValue;
      },
      {
        ...validatorContext,
        messageTemplate: (validatorContext && validatorContext.messageTemplate) || RangeValidator.messageTemplate,
      }
    );
  }

  static create(group: string, context?: Partial<ValidationContextBase>): RangeValidator {
    return new RangeValidator(group, context);
  }
}

export class InvalidValuesValidator extends SyncValidator {
  private static messageTemplate = "The value '%value%' is not a valid input for %displayName%";

  constructor(group: string, validatorContext?: Partial<ValidationContextBase>) {
    super(
      'invalidValues',
      group,
      (value, context) => {
        if (value == null) {
          return true;
        }
        return !(context.values || []).some((v: any) => this.test(v, value));
      },
      {
        ...validatorContext,
        messageTemplate:
          (validatorContext && validatorContext.messageTemplate) || InvalidValuesValidator.messageTemplate,
      }
    );
  }

  static create(group: string, context?: Partial<ValidationContextBase>): InvalidValuesValidator {
    return new InvalidValuesValidator(group, context);
  }

  private test(expression: string, value: string): boolean {
    const match = expression.match(/\/(.*)\//);
    if (match) {
      return new RegExp(match[1]).test(value);
    }

    return expression === value;
  }
}

export class CurrencyValidator extends SyncValidator {
  private static messageTemplate = '%displayName% must be a valid currency amount';

  private regExpValidator: SyncValidator;

  constructor(group: string, validatorContext?: Partial<ValidationContextBase>) {
    super(
      'currency',
      group,
      (value, context) => {
        if (value == null) {
          return true;
        }
        if (typeof value === 'number') {
          return true;
        }

        return typeof value === 'string' && !this.regExpValidator.validate(value, context);
      },
      {
        ...validatorContext,
        messageTemplate: (validatorContext && validatorContext.messageTemplate) || CurrencyValidator.messageTemplate,
      }
    );

    this.regExpValidator = makeCurrencyValidator(group, validatorContext);
  }

  static create(group: string, context?: Partial<ValidationContextBase>): CurrencyValidator {
    return new CurrencyValidator(group, context);
  }
}

export class NumericValidator extends SyncValidator {
  private regExpValidator: SyncValidator;

  constructor(group: string, validatorContext?: Partial<ValidationContextBase>) {
    const regExpValidator = makeNumericValidator(group, validatorContext);
    const messageTemplate = regExpValidator.context.messageTemplate;

    super(
      'numeric',
      group,
      (value, context) => {
        if (value == null) {
          return true;
        }
        if (typeof value === 'number') {
          return context && context.allowDecimals ? true : Number.isInteger(value);
        }

        return typeof value === 'string' && !this.regExpValidator.validate(value, context);
      },
      {
        ...validatorContext,
        messageTemplate: (validatorContext && validatorContext.messageTemplate) || messageTemplate,
      }
    );

    this.regExpValidator = regExpValidator;
  }

  static create(group: string, context?: Partial<ValidationContextBase>): NumericValidator {
    return new NumericValidator(group, context);
  }
}

export class CompareValidator extends SyncValidator {
  constructor(group: string, validatorContext?: Partial<ValidationContextBase>) {
    super(
      'compare',
      group,
      (value, context) => {
        if (value == null) {
          return true;
        }
        const otherValue = context.model[context.other];
        const op = context.op;

        if (op === '==') {
          return value === otherValue;
        } else if (op === '<') {
          return value < otherValue;
        } else if (op === '<=') {
          return value <= otherValue;
        } else if (op === '>') {
          return value > otherValue;
        } else if (op === '>=') {
          return value >= otherValue;
        } else if (op === '!=') {
          return value !== otherValue;
        }

        return false;
      },
      {
        ...validatorContext,
        messageTemplate: (validatorContext && validatorContext.messageTemplate) || 'Invalid value',
      }
    );
  }

  static create(group: string, context?: Partial<ValidationContextBase>): CompareValidator {
    return new CompareValidator(group, context);
  }
}

export const makeZipCodeValidator = (group: string, context?: Partial<ValidationContextBase>): RegExpValidator => {
  context = {
    ...context,
    expression: /^\d{5}([\-]\d{4})?$/,
    messageTemplate: '%displayName% is not a valid U.S. zipcode',
  };

  return RegExpValidator.create('zipCode', group, context);
};

export const makeEmailValidator = (group: string, context?: Partial<ValidationContextBase>): RegExpValidator => {
  // tslint:disable-next-line:max-line-length
  const reEmailAddress =
    /^((([a-z]|\d|[!#\$%&'\*\+\-\/=\?\^_`{\|}~]|[\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF])+(\.([a-z]|\d|[!#\$%&'\*\+\-\/=\?\^_`{\|}~]|[\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF])+)*)|((\x22)((((\x20|\x09)*(\x0d\x0a))?(\x20|\x09)+)?(([\x01-\x08\x0b\x0c\x0e-\x1f\x7f]|\x21|[\x23-\x5b]|[\x5d-\x7e]|[\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF])|(\\([\x01-\x09\x0b\x0c\x0d-\x7f]|[\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF]))))*(((\x20|\x09)*(\x0d\x0a))?(\x20|\x09)+)?(\x22)))@((([a-z]|\d|[\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF])|(([a-z]|\d|[\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF])([a-z]|\d|-|\.|_|~|[\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF])*([a-z]|\d|[\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF])))\.)+(([a-z]|[\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF])|(([a-z]|[\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF])([a-z]|\d|-|\.|_|~|[\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF])*([a-z]|[\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF])))\.?$/i;

  context = {
    ...context,
    expression: reEmailAddress,
    messageTemplate: '%displayName% is not a valid email address',
  };

  return RegExpValidator.create('email', group, context);
};

export const makePhoneValidator = (group: string, context?: Partial<ValidationContextBase>): RegExpValidator => {
  context = {
    ...context,
    // Too forgiving
    // expression: /^((\+|(0(\d+)?[-/.\s]?))[1-9]\d{0,2}[-/.\s]?)?((\(\d{1,6}\)|\d{1,6})[-/.\s]?)?(\d+[-/.\s]?)+\d+$/,
    // The following forces 10-digit US w/ no country code. You can insert punctuation and spaces
    // We will strip these out before sending to SP.
    expression: /^\(?\d{3}\)?[\s.-]?\d{3}[\s.-]?\d{4}$/,
    messageTemplate: '%displayName% must be 10-digit phone number',
  };

  return RegExpValidator.create('phone', group, context);
};

export const makeCurrencyValidator = (group: string, context?: Partial<ValidationContextBase>): RegExpValidator => {
  context = {
    ...context,
    expression: /^[+-]?[0-9]{1,3}(?:,?[0-9]{3})*(?:\.[0-9]{2})?$/,
    messageTemplate: '%displayName% must be a valid currency amount',
  };

  return RegExpValidator.create('currency', group, context);
};

export const makeSocialSecurityNumberValidator = (
  group: string,
  context?: Partial<ValidationContextBase>
): RegExpValidator => {
  context = {
    ...context,
    expression: /^(\d{3}-\d{2}-\d{4})|(\d{3}\d{2}\d{4})$/,
    messageTemplate: '%displayName% must be a valid Social Security number',
  };

  return RegExpValidator.create('social', group, context);
};

export const makeNumericValidator = (group: string, context?: Partial<ValidationContextBase>): RegExpValidator => {
  const messageTemplate = '%displayName% must be a numeric value';
  const messageTemplateLeadingZeros = '%displayName% must be a numeric value with optional leading zeros';

  context = {
    ...context,
    expression:
      context && context.allowDecimals
        ? context && context.allowLeadingZeros
          ? /^([0-9]*)(\.[0-9]*)?$/
          : /^(0)|([1-9][0-9]*)(\.[0-9]*)?$/
        : context && context.allowLeadingZeros
        ? /^([0-9]*)$/
        : /^(0)|([1-9][0-9]*)$/,
    messageTemplate:
      (context && context.messageTemplate) || (context && context.allowLeadingZeros)
        ? messageTemplateLeadingZeros
        : messageTemplate,
  };

  return RegExpValidator.create('numeric', group, context);
};

export class ExpressionValidator extends SyncValidator {
  constructor(group: string, validatorContext?: Partial<ValidationContextBase>) {
    super(
      'expression',
      group,
      (value, context) => {
        if (value == null) {
          return true;
        }

        try {
          const unitOfWork: UnitOfWorkBase = context.unitOfWork;
          const store = (entityType: string): Entity[] => unitOfWork.getEntities(entityType);
          /**
           * SECURITY: Safe because text passed to `Function` comes from our server, not the user
           */
          if (!context.expressionFn) {
            const evalFunc = `function(store, context, model, isClient, isServer){ return ${parseOptionalChaining(
              context.expression
            )} }`;
            context.expressionFn = Function(`"use strict";return (${evalFunc})`)();
          }
          return context.expressionFn(store, context, context.model, context.isClient, context.isServer);
        } catch (e) {
          const message =
            `Expression '${context.expression}' failed for validator '${this.name}'` +
            ` and model type '${context.modelType}' with error '${e.message}'`;
          throw new Error(message);
        }
      },
      {
        ...validatorContext,
        messageTemplate: (validatorContext && validatorContext.messageTemplate) || 'Invalid value',
      }
    );
  }

  static create(group: string, context?: Partial<ValidationContextBase>): ExpressionValidator {
    return new ExpressionValidator(group, context);
  }
}

export const defaultValidatorProviders: (ValueProvider | FactoryProvider)[] = [
  {
    provide: VALIDATOR_FACTORY,
    useValue: { name: 'required', fn: RequiredValidator.create },
    multi: true,
  },
  {
    provide: VALIDATOR_FACTORY,
    useValue: { name: 'minLength', fn: MinLengthValidator.create },
    multi: true,
  },
  {
    provide: VALIDATOR_FACTORY,
    useValue: { name: 'maxLength', fn: MaxLengthValidator.create },
    multi: true,
  },
  {
    provide: VALIDATOR_FACTORY,
    useValue: { name: 'zipCode', fn: makeZipCodeValidator },
    multi: true,
  },
  {
    provide: VALIDATOR_FACTORY,
    useValue: { name: 'phone', fn: makePhoneValidator },
    multi: true,
  },
  {
    provide: VALIDATOR_FACTORY,
    useValue: { name: 'email', fn: makeEmailValidator },
    multi: true,
  },
  {
    provide: VALIDATOR_FACTORY,
    useValue: { name: 'currency', fn: CurrencyValidator.create },
    multi: true,
  },
  {
    provide: VALIDATOR_FACTORY,
    useValue: { name: 'social', fn: makeSocialSecurityNumberValidator },
    multi: true,
  },
  {
    provide: VALIDATOR_FACTORY,
    useValue: { name: 'compare', fn: CompareValidator.create },
    multi: true,
  },
  {
    provide: VALIDATOR_FACTORY,
    useValue: { name: 'range', fn: RangeValidator.create },
    multi: true,
  },
  {
    provide: VALIDATOR_FACTORY,
    useValue: { name: 'numeric', fn: NumericValidator.create },
    multi: true,
  },
  {
    provide: VALIDATOR_FACTORY,
    useValue: { name: 'invalidValues', fn: InvalidValuesValidator.create },
    multi: true,
  },
  {
    provide: VALIDATOR_FACTORY,
    useValue: { name: 'expression', fn: ExpressionValidator.create },
    multi: true,
  },
];
